import axios, {
    type AxiosInstance,
    type AxiosPromise,
    type AxiosRequestConfig,
    type AxiosResponse,
} from "axios";
import { type ThunkDispatch } from "redux-thunk";
import { history, store } from "../../_Bootstrap/RoutingAndStore";
import { offline, online } from "../../Actions/Information/InformationActions";
import { userLogout } from "../../Actions/Users/UserActions";
import PopupManager from "../../Common/Popups/PopupManager";
import { runUploadQueue } from "../../Workers/Upload/UploadAgent";
import UUID from "../Generic/UUID";
import { getLogLevel, LogLevel } from "./Logging";
import { getOption } from "./Options/Options";

// Create the request object
let token: undefined | string = undefined;
let expectingRateLimiter = false;
let rateLimiterClearer: undefined | ReturnType<typeof setTimeout>;
let expectingPaymentRequired = false;
let paymentRequiredClearer: undefined | ReturnType<typeof setTimeout>;
let multiplePaymentClearer = false;

if(typeof document !== "undefined") {
    const meta = document.querySelector("meta[name=csrf-token]");

    if(meta) {
        token = (meta as HTMLMetaElement).content;
    }
}

const Request = axios.create({
    headers: {
        "X-CSRF-TOKEN"       : token as string,
        "X-Requested-With"   : "XMLHttpRequest",
        "X-Shopfront-Version": (typeof __VERSION__ === "undefined" ? { commit: "Unknown" } : __VERSION__).commit,
    },
});

// Add the interceptor to ensure we haven't been logged out
Request.interceptors.response.use(function(response) {
    const state = store.getState();

    // Check if we were offline
    if(!state.information.online) {
        // Run the upload queue
        runUploadQueue();
    }

    // Go online
    (store.dispatch as ThunkDispatch<any, any, any>)(online());

    return response;
}, (err) => {
    if(!axios.isCancel(err)) {
        if(!err.response) {
            (store.dispatch as ThunkDispatch<any, any, any>)(offline());
        } else {
            if(err.response.status === 0) {
                (store.dispatch as ThunkDispatch<any, any, any>)(offline());
            } else if(err.response.status === 401) {
                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.error("You're not logged in, logging out because of normal request.");
                    console.log("The logout response was:", err.response.data);
                }

                (store.dispatch as ThunkDispatch<any, any, any>)(userLogout());
            } else if(err.response.status === 403) {
                console.error("You're forbidden to access this resource.", err.response.data);
            } else if(err.response.status === 402) {
                console.error("Your vendor has expired");

                if(expectingPaymentRequired) {
                    console.log("Expired vendor is expected");

                    if(!multiplePaymentClearer) {
                        expectingPaymentRequired = false;

                        if(paymentRequiredClearer) {
                            clearTimeout(paymentRequiredClearer);
                            paymentRequiredClearer = undefined;
                        }
                    }

                    throw new PaymentRequiredError();
                } else if(err.response.data?.onIce === true) {
                    history.push("/expired?ice=true");
                } else {
                    history.push("/expired");
                }
            } else if(err.response.status === 429) {
                if(expectingRateLimiter && err.config) {
                    expectingRateLimiter = false;

                    if(rateLimiterClearer) {
                        clearTimeout(rateLimiterClearer);
                        rateLimiterClearer = undefined;
                    }

                    return new Promise((res, rej) => {
                        setTimeout(() => {
                            Request.request(err.config)
                                .then(res)
                                .catch(rej);
                        }, 30 * 1000); // 30 seconds
                    });
                }

                console.error("Rate limiter hit");
                PopupManager.warning("You've hit our rate limiter, if this is happening often, please contact us");
            } else if(err.response.status === 540 || err.response.status === 541) { // Custom Shopfront Codes
                console.error("Shopfront is down");
                (store.dispatch as ThunkDispatch<any, any, any>)(offline());
            }
        }
    }

    return Promise.reject(err);
});

// Add the interceptor to send the instance ID
Request.interceptors.request.use(config => {
    if(forcedOfflineRequests) {
        throw new Error("Forced offline mode");
    }

    if(!config.headers) {
        config.headers = {};
    }

    const currentState = store.getState();

    config.headers["Shopfront-Instance-ID"] = getOption("_instance_id") ?? UUID.generate();

    const outletId = currentState.location.outlet?.id;
    const registerId = currentState.location.register?.id;

    if(outletId) {
        config.headers["Shopfront-Outlet-ID"] = outletId;
    }

    if(registerId) {
        config.headers["Shopfront-Register-ID"] = registerId;
    }

    return config;
}, err => {
    return Promise.reject(err);
});

export default Request;

export function expectRateLimiter(): void {
    expectingRateLimiter = true;

    if(rateLimiterClearer) {
        clearTimeout(rateLimiterClearer);
    }

    rateLimiterClearer = setTimeout(() => {
        expectingRateLimiter = false;
    }, 30 * 1000); // Whilst this probably doesn't need the full 30 seconds, it's there just in case
}

export class PaymentRequiredError extends Error {}

export function expectPaymentRequired(): void {
    expectingPaymentRequired = true;

    if(paymentRequiredClearer) {
        clearTimeout(paymentRequiredClearer);
        multiplePaymentClearer = true;
    }

    paymentRequiredClearer = setTimeout(() => {
        expectingPaymentRequired = false;
        multiplePaymentClearer = false;
    }, 30 * 1000);
}

let forcedOfflineRequests = false;
let forcedOfflineClearer: null | ReturnType<typeof setTimeout> = null;

export function forceOfflineRequests(): void {
    forcedOfflineRequests = true;

    if(forcedOfflineClearer) {
        clearTimeout(forcedOfflineClearer);
    }

    forcedOfflineClearer = setTimeout(() => {
        forcedOfflineRequests = false;
    }, 30 * 1000);
}

/**
 * Get a value from the cookies
 * @param name
 * @link https://stackoverflow.com/a/15724300
 */
export const getCookie = (name: string): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if(parts.length === 2) return parts.pop()?.split(";").shift();
};

export class InvalidStreamError extends Error {
    public type: string;
    public response: Response;

    constructor(message: string, type: string, response: Response) {
        super(message);

        this.type = type;
        this.response = response;
    }
}

export class ServerError extends Error {
    public status: number;
    public response: Response;

    constructor(message: string, status: number, response: Response) {
        super(message);

        this.status = status;
        this.response = response;
    }
}

/**
 * Stream the response from a URL
 * @param {string} url
 * @param expected
 * @param postFields
 * @returns {Promise<ReadableStream>}
 */
export function streamResponse(
    url: string | URL,
    expected?: string,
    postFields?: object
): Promise<ReadableStream> {
    const accepts = [ "application/json" ];

    if(expected && expected !== "application/json") {
        accepts.push(expected);
    }

    const options: RequestInit = {
        headers: {
            "Accept": accepts.join(","),
        },
    };

    if(typeof postFields !== "undefined") {
        options.method = "POST";
        (options.headers as any)["Content-Type"] = "application/json";
        options.body = JSON.stringify(postFields);
    }

    return fetch(url, options)
        .then(response => {
            if(response.status !== 200) {
                throw new ServerError("Invalid response returned from the server", response.status, response);
            }

            if(response.headers.has("content-type")) {
                if((response.headers.get("content-type") as string).includes("application/json")) {
                    throw new InvalidStreamError(
                        "JSON content type is not streamable",
                        response.headers.get("content-type") as string,
                        response
                    );
                }
            }

            if(!(response.body instanceof ReadableStream)) {
                throw new InvalidStreamError(
                    "Response body is not a readable stream",
                    response.headers.get("content-type") as string,
                    response
                );
            }

            if(expected) {
                if(!response.headers.has("content-type")) {
                    throw new InvalidStreamError("Content type not provided with respond", "Not Provided", response);
                }

                if(!(response.headers.get("content-type") as string).includes(expected)) {
                    throw new InvalidStreamError(
                        "Content type is not compatible with expected type",
                        response.headers.get("content-type") as string,
                        response
                    );
                }
            }

            return response.body;
        });
}

/**
 * Download a stream
 * @param {ReadableStream} stream
 * @param filename
 * @returns {Promise<boolean>}
 */
export async function downloadStream(stream: ReadableStream, filename: string): Promise<void> {
    // TODO: Download streams by streaming the data
    const response = new Response(stream);

    return response.blob()
        .then(blob => URL.createObjectURL(blob))
        .then(url => {
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", filename);
            link.click();

            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);
        });
}

export type PostData = Record<string, Array<string | number | boolean | Blob> | string | number | boolean | Blob>;

/**
 * Upload a set of files to the provided URL
 * @param url
 * @param postData
 * @returns {AxiosPromise<any>}
 */
export function uploadFiles<R = any>(
    url: string,
    postData: PostData
): AxiosPromise<R> {
    const formData = new FormData();

    for(const i in postData) {
        if(!Object.prototype.hasOwnProperty.call(postData, i)) continue;

        let data = postData[i];

        if(Array.isArray(data)) {
            for(let j = 0, n = data.length; j < n; j++) {
                if(!(data[j] instanceof Blob)) {
                    data[j] = data[j].toString();
                }

                formData.append(i, data[j] as string | Blob);
            }
        } else {
            if(!(data instanceof Blob)) {
                data = data.toString();
            }

            formData.append(i, data);
        }
    }

    return Request.post<R>(url, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
}

let vendorSubdomain: string | undefined;

/**
 * Get the Vendor's subdomain
 * @returns {string}
 */
export function getVendorSubdomain(): string {
    if(!vendorSubdomain) {
        vendorSubdomain = window.location.hostname.split(".")[0];
    }

    return vendorSubdomain;
}

let mainHost: string | undefined;

/**
 * Get the main URL for Shopfront
 */
export function getMainHost(): string {
    if(!mainHost) {
        mainHost = window.location.hostname.split(".").slice(1).join(".");
    }

    return mainHost;
}

const cached = new Map();

/**
 * Drop in replacement for Request which caches responses for five minutes
 */
class CacheRequestProxy implements ProxyHandler<object> {
    protected overrideGet(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        const key = JSON.stringify({ url, config });

        if(cached.has(key)) {
            const cache = cached.get(key);

            return Promise.resolve(cache.request);
        }

        const request = Request.get(url, config);

        const timeout = setTimeout(() => {
            cached.delete(key);
        }, 300000);

        cached.set(key, {
            request,
            timeout,
        });

        return request;
    }

    protected post<R = any>(
        url: string,
        data: any,
        config?: AxiosRequestConfig,
        refresh = false
    ): Promise<AxiosResponse<R>> {
        const key = JSON.stringify({ url, data, config });

        if(cached.has(key) && !refresh) {
            const cache = cached.get(key);

            return Promise.resolve(cache.request);
        }

        if(typeof config?.signal !== "undefined") {
            config.signal.addEventListener("abort", () => {
                cached.delete(key);
            }, {
                once: true,
            });
        }

        const request = Request.post<R>(url, data, config);
        const timeout = setTimeout(() => {
            cached.delete(key);
        }, 300000);

        cached.set(key, {
            request,
            timeout,
        });

        return request;
    }

    public get(target: object, prop: PropertyKey): any {
        if(typeof this[prop as keyof CacheRequestProxy] !== "undefined") {
            if(prop === "get") {
                return this.overrideGet;
            }

            return this[prop as keyof CacheRequestProxy];
        }

        if(typeof Request[prop as keyof typeof Request] !== "undefined") {
            return Request[prop as keyof typeof Request];
        }

        throw new TypeError(`Property ${String(prop)} not found`);
    }

    public clearCache(): void {
        cached.clear();
    }
}

function createCacheRequest(): AxiosInstance & { clearCache?: () => void; } {
    if(process.env.NODE_ENV === "test") {
        return Request;
    } else {
        return new Proxy<AxiosInstance>({} as any, new CacheRequestProxy());
    }
}

export const CacheRequest = createCacheRequest();
